import type { ReactNode } from 'react';

import {
  AppBar,
  Box,
  Button,
  IconButton,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { X } from 'lucide-react';

import { useLocale } from '@shared/hooks/locale';

export const SelectionPickerHeader = ({
  title,
  tabs,
  onClose,
  selectionCount,
  onClear,
  onSave,
  saving,
}: {
  title: ReactNode;
  tabs?: ReactNode;
  onClose: () => void;
  selectionCount: number;
  onClear: () => void;
  onSave: () => void;
  saving: boolean;
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useLocale();

  const buttons = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Button
        variant="text"
        disabled={selectionCount === 0}
        sx={{ mr: 1 }}
        onClick={onClear}
      >
        {t('Clear')}
      </Button>

      <Button
        variant="contained"
        color="primary"
        loading={saving}
        loadingPosition="start"
        startIcon={
          <Box
            sx={{
              borderRadius: '50px',
              fontSize: '12px !important',
              padding: '0 8px',
              backgroundColor: (theme.vars ?? theme).palette.primary.dark,
            }}
          >
            {saving ? null : selectionCount}
          </Box>
        }
        onClick={onSave}
      >
        <Typography variant="button">
          {isMobile ? t('Save') : t('Save selection')}
        </Typography>
      </Button>
    </Box>
  );

  return (
    <AppBar color="inherit" elevation={0} position="sticky">
      <Toolbar
        sx={{
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          borderBottom: `2px solid ${(theme.vars ?? theme).palette.divider}`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexGrow: 1,
          }}
        >
          <IconButton edge="start" color="inherit" onClick={onClose}>
            <X size={20} />
          </IconButton>
          <Box
            sx={{
              ...theme.typography.h6,
              fontWeight: theme.text.font.bold,
              flexGrow: { xs: 1, sm: 0 },
            }}
          >
            {title}
          </Box>
          {tabs == null ? (
            <Box sx={{ flexGrow: 1 }} />
          ) : (
            !isMobile && (
              <Box
                sx={{
                  flexGrow: 1,
                  justifyContent: 'center',
                  display: 'flex',
                }}
              >
                {tabs}
              </Box>
            )
          )}
          {buttons}
        </Box>
        {tabs != null && isMobile && <div css={{ marginLeft: 20 }}>{tabs}</div>}
      </Toolbar>
    </AppBar>
  );
};
